import React, { useState, useEffect, useMemo } from "react";
import {
  DialogContent,
  DialogActions,
  Grid,
  Accordion,
  AccordionDetails,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  useTheme,
} from "@mui/material";
import { FlightChipCollection } from "src/components/Flights/FlightChip";
import { ModalTitle, ModalButton } from "../DialogModalComponents";
import { LoadingMessage } from "../../LoadingMessage/LoadingMessage";
import { useDispatch, useSelector } from "react-redux";
import {
  IFlight,
  IGroundEvent,
  IAPISRule,
  APISMessageResult,
  ISendAPISMessage,
  IAPISConfig,
  DocumentIdentifiers,
  ISendAPISMessageType,
} from "@vmosolutions/oc-interfaces";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { isFlight } from "src/redux/reducers/FlightTimelineReducer";
import { EventModalTypes } from "src/components/DialogModal/DialogModalWrapper";
import { FormikProps, Form, Formik, FormikHelpers } from "formik";
// Yup Validation
import * as Yup from "yup";
import { getAPISRules } from "src/redux/actions/APIS/APIS.Data";
import { FormikSelectField } from "../../FormikComponents/FormikWrappers";
import { SpinnerProgress } from "../../SpinnerProgress/SpinnerProgress";
import AmplifyAPIWrapper, { INotifications } from "src/API/AmplifyAPIWrapper";
import { download } from "src/utils/downloadTextFile";
import { setSelectedEvents } from "src/redux/actions/flights/FlightTimeline.Actions";
import { DialogModalStyles } from "../DialogModalWrapper";

interface ISendAPISMessageProps {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal?: (EventModalTypes: EventModalTypes) => void;
  id?: string;
  flight?: IFlight; //case where you want to overide the selected flight from redux
}

type SendAPISMessageForm = FormikProps<ISendAPISMessage>;

// Operator Form Validation with YUP
const SendAPISMessageSchema = Yup.object().shape({
  overrideMessage: Yup.boolean().required("Override is required"),
  messagesToSend: Yup.array()
    .of(
      Yup.object().shape({
        result: Yup.string().nullable(),
        documentIdentifier: Yup.mixed().oneOf([...Object.values(DocumentIdentifiers), null]),
      })
    )
    .required()
    .min(1, "At least one message required"),
});

const regex = /^[^_]*_/;
const regex2 = /_MESSAGE/;

const formatLabel = (value: string): string => {
  return value.replace(regex, " ").replace(regex2, "");
};
export const getAPISDisplay = (apisRule: IAPISRule): any => {
  const { result } = apisRule;
  switch (result) {
    case APISMessageResult.CanArrivalMessage:
      return { country: "Canada", messageType: "Arrival" };
    case APISMessageResult.CanDepartureMessage:
      return { country: "Canada", messageType: "Departure" };
    case APISMessageResult.USArrivalMessage:
      return { country: "United States", messageType: "Arrival" };
    case APISMessageResult.USDepartureMessage:
      return { country: "United States", messageType: "Departure" };
    case APISMessageResult.USOverflightMessage:
      return { country: "United States", messageType: "Overflight" };
    case APISMessageResult.UKArrivalMessage:
      return { country: "United Kingdom", messageType: "Arrival" };
    case APISMessageResult.UKDepartureMessage:
      return { country: "United Kingdom", messageType: "Departure" };
  }
};

interface IAPISTableData {
  country: string;
  messageType: string;
  index: number;
  value: IAPISRule;
}

interface IDocumentIdentOptions {
  value: string;
  label: DocumentIdentifiers;
}

interface IMessageOptions {
  value: string;
  label: APISMessageResult;
}

export interface IRulesMap {
  [country: string]: {
    messageOptions: Set<APISMessageResult>;
    documentIdentifiers: IDocumentIdentOptions[];
  };
}

const getMessageOptions = (messageTypes: Set<APISMessageResult>): IMessageOptions[] =>
  Array.from(messageTypes)
    .map((type: APISMessageResult, index: number) => ({
      value: index.toString(),
      label: type,
    }))
    .sort((a, b) => {
      return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
    });

const getEnumKeys = (value: string, enums: any): string => {
  const found = Object.keys(enums).find((key: any) => enums[key] === value);
  return found.replace(/^[^_]*_/, " ");
};

export const SendAPISMessageModal = (props: ISendAPISMessageProps): JSX.Element => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const selectedEvents: (IFlight | IGroundEvent)[] = useSelector(
    (state: IRootState) => state.timelineFlights.selectedEvents
  );
  const { flight } = props;
  // const mosaicPane = useMemo(() => {
  //   return props.id ? document.getElementById(`${props.id}-panel`) : null;
  // }, [props.id]);

  // For BULK batch tracking
  const [building, setBuilding] = useState<boolean>(false);
  const [buildStatus, setBuildStatus] = useState<string>(null);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [mappedRules, setMappedRules] = useState<IRulesMap>({});

  const selectedFlights: IFlight[] = React.useMemo(() => {
    if (flight) {
      return [flight];
    }
    return selectedEvents.filter((event: IFlight | IGroundEvent) => isFlight(event));
  }, [flight, selectedEvents]);

  const typeFlight: boolean = selectedFlights.length > 0 ? true : false;

  const initialValues: ISendAPISMessage = useMemo(() => {
    if (!mappedRules) {
      return { overrideMessage: true, messagesToSend: [] };
    }
    const messagesToSend = Object.keys(mappedRules).map(() => ({
      result: null,
      documentIdentifier: null,
    }));
    return { overrideMessage: true, messagesToSend: messagesToSend };
  }, [mappedRules]);

  useEffect(() => {
    const getAPISData = async (): Promise<void> => {
      setIsLoading(true);
      const flightId = selectedFlights[0]?._id;
      if (!flightId) {
        return;
      }
      const response = await getAPISRules();
      const apisRules: IAPISRule[] = response?.rules;

      const ruleMap: IRulesMap = {};
      apisRules?.forEach((rule: IAPISRule) => {
        ruleMap[rule.country] = {
          messageOptions: ruleMap[rule.country]
            ? ruleMap[rule.country].messageOptions.add(rule.result)
            : new Set([rule.result]),
          documentIdentifiers: null,
        };
      });
      response?.settings.forEach((setting: IAPISConfig) => {
        // Skip countries that don't have rules as they won't be applicable for message generation
        if (!ruleMap[setting.country]) {
          return;
        }
        const options: IDocumentIdentOptions[] = setting.documentIdentifiers.map(
          (ident: DocumentIdentifiers, index: number): IDocumentIdentOptions => ({
            value: index.toString(),
            label: ident,
          })
        );
        ruleMap[setting.country].documentIdentifiers = options;
      });
      setMappedRules(ruleMap);
      setIsLoading(false);
    };

    getAPISData();
  }, [selectedFlights]);

  const handleCancel = (): void => {
    // If a bulk operation was started and users choose to close the modal, clear all selected events making it easier for them to start a new bulk operation.
    if (buildStatus) dispatch(setSelectedEvents([]));
    props.handleClose(false);
    props.setActiveEventModal?.(EventModalTypes.None);
  };

  const handleConfirm = async (
    values: ISendAPISMessage,
    formikHelpers: FormikHelpers<ISendAPISMessage>
  ): Promise<void> => {
    setBuildStatus(null);
    const { setSubmitting, resetForm } = formikHelpers;

    if (typeFlight) {
      const flightId = selectedFlights[0]?._id;
      if (!flightId) {
        return;
      }
      setBuilding(true);

      // setBuildStatus("Generating APIS message");
      const notifications: INotifications = {
        dispatch: dispatch,
        infoMessage: "Generating APIS message...",
        successMessage: "Message successfully generated",
        errorMessage: "Error: Something Went Wrong",
      };
      const response = await AmplifyAPIWrapper.Post<any>(`/generateAPIS/${flightId}`, values, notifications);
      if (response?.apisMessages) {
        let success = 0;
        response?.apisMessages?.forEach((each: any) => {
          if (!each) {
            return;
          }
          if (each.generatedMessage) success++;
        });
        setBuildStatus(`${success} ${success > 1 ? "messages" : "message"} created. To view, visit the content tab`);
      }
      if (response?.error) {
        setBuildStatus("Error: Something Went Wrong");
      }
      setBuilding(false);
      setSubmitting(false);
      resetForm({ values });
    }
  };

  return (
    <DialogContent sx={DialogModalStyles(theme)}>
      <div className="modal_content" style={{ flexGrow: 1 }}>
        {!!typeFlight && <FlightChipCollection flights={selectedFlights} />}

        <ModalTitle title={"Send APIS Message"}></ModalTitle>
        <SpinnerProgress className="spinner" loading={isLoading} message="loading apis message options" />
        <Formik
          initialValues={initialValues}
          enableReinitialize={true}
          onSubmit={handleConfirm}
          validationSchema={SendAPISMessageSchema}
        >
          {(form: SendAPISMessageForm) => {
            const { handleReset, dirty } = form;

            const isFullFormValid = (): boolean => {
              const fieldValues: ISendAPISMessageType[] = form.getFieldProps("messagesToSend")?.value;
              let valid = true;
              fieldValues.forEach((each: ISendAPISMessageType) => {
                if ((!each.documentIdentifier && each.result) || (each.documentIdentifier && !each.result)) {
                  valid = false;
                }
              });
              return valid;
            };

            const isViewValid = (index: number): boolean => {
              const fieldValues: ISendAPISMessageType = form.getFieldProps(`messagesToSend[${index}]`)?.value;
              // console.log("test", fieldValues);
              if (fieldValues?.documentIdentifier && fieldValues?.result) {
                return true;
              } else return false;
            };
            const handleView = async (index: number): Promise<void> => {
              setBuildStatus(null);
              const flightId = selectedFlights[0]?._id;
              if (!flightId) {
                return;
              }
              const fieldValues: ISendAPISMessageType = form.getFieldProps(`messagesToSend[${index}]`)?.value;

              const notifications: INotifications = {
                dispatch: dispatch,
                infoMessage: "Creating APIS view message...",
                successMessage: "Message successfully created",
                errorMessage: "Error: Something Went Wrong",
              };

              const response = await AmplifyAPIWrapper.Post<any>(`/APIS/view/${flightId}`, fieldValues, notifications);
              if (response?.error) {
                setBuildStatus(response.error);
              }
              if (response?.message) {
                download(`${fieldValues.result}.txt`, response.message);
              }
            };
            return (
              <Form>
                <Grid container direction="column">
                  <Grid item>
                    <Accordion expanded={form.values.overrideMessage} disabled={!form.values.overrideMessage}>
                      <AccordionDetails className={"accordianDetails"}>
                        <TableContainer>
                          <Table aria-label="simple table" size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell align="center">Country</TableCell>
                                <TableCell align="center">Message Type</TableCell>
                                <TableCell align="center">Document Identifier</TableCell>
                                <TableCell align="center"></TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.keys(mappedRules)?.map((country: string, index: number) => (
                                <TableRow key={index}>
                                  <TableCell align="center">{country}</TableCell>
                                  <TableCell align="left">
                                    <FormikSelectField
                                      className="field selectfield"
                                      name={`messagesToSend[${index}].result`}
                                      parse={(option: IMessageOptions) => option.label}
                                      format={(option: string) =>
                                        getMessageOptions(mappedRules[country]?.messageOptions)?.find(
                                          (opt: IMessageOptions) => opt.label === option
                                        ) ?? null
                                      }
                                      // label="Rider"
                                      getOptionLabel={(opt: any) => {
                                        return formatLabel(opt.label);
                                      }}
                                      options={getMessageOptions(mappedRules[country]?.messageOptions) ?? []}
                                      closeMenuOnSelect={false}
                                      hideSelectedOptions={false}
                                      isSearchable={true}
                                      isClearable={false}
                                      menuPosition="fixed"
                                    />
                                  </TableCell>
                                  <TableCell align="left">
                                    <FormikSelectField
                                      className="field selectfield"
                                      name={`messagesToSend[${index}].documentIdentifier`}
                                      parse={(option: IDocumentIdentOptions) => {
                                        return option.label;
                                      }}
                                      format={(option: string) =>
                                        mappedRules[country]?.documentIdentifiers.find(
                                          (ident: IDocumentIdentOptions) => ident.label === option
                                        ) ?? null
                                      }
                                      getOptionLabel={(option: any) =>
                                        `${option.label} - ${getEnumKeys(option.label, DocumentIdentifiers)}`
                                      }
                                      options={
                                        mappedRules[country]?.documentIdentifiers?.length
                                          ? mappedRules[country].documentIdentifiers.sort((a, b) => {
                                              return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
                                            })
                                          : []
                                      }
                                      closeMenuOnSelect={false}
                                      hideSelectedOptions={false}
                                      isSearchable={true}
                                      isClearable={false}
                                      menuPosition="fixed"
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <ModalButton
                                      label={"View"}
                                      disable={!isViewValid(index)}
                                      handler={() => {
                                        handleView(index);
                                      }}
                                    ></ModalButton>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  </Grid>
                  <DialogActions>
                    <Button type="reset" disabled={building || !dirty} onClick={handleReset}>
                      Reset
                    </Button>
                    <ModalButton label={buildStatus ? "Close" : "Cancel"} handler={handleCancel}></ModalButton>
                    <Button type="submit" disabled={building || !dirty || !isFullFormValid() || form.isSubmitting}>
                      Send
                    </Button>
                  </DialogActions>
                </Grid>
              </Form>
            );
          }}
        </Formik>

        <div className="modal_status">
          {!!buildStatus && <LoadingMessage message={buildStatus} animate={building} />}
        </div>
      </div>
    </DialogContent>
  );
};
